/**
 * Editor operations behind the toolbar, menus and keyboard shortcuts: each combines the store, the pure scene
 * commands and the asset library.
 */
import type { Asset, Element, Scene } from '@prism-graphics/scene-schema';
import { api, type CheckReport, type LibraryAsset } from '../api.ts';
import { t } from '../i18n.ts';
import { addElements, deleteElements, duplicate, ensureAsset, groupElements, moveBy, moveElements, patchElement, reorder, ungroup, type InsertAt } from '../model/commands.ts';
import { copySelection, parseClipboard, paste, serializeClipboard, type ClipboardPayload } from '../model/clipboard.ts';
import { createElement, nominalSize, type NewElementKind } from '../model/factory.ts';
import { exposeField, type Exposable } from '../model/fields.ts';
import { align, distribute, unionRect, type Alignment } from '../model/snapping.ts';
import { allIds, findElement, indexIn, isContainer, parentOf, pathTo, siblingsOf, topmost } from '../model/tree.ts';
import { assetCache } from './assets.ts';
import { brandCache } from './brand.ts';
import type { EditorStore } from './store.ts';

/** Where new content goes: into a selected container, after a selected element, or on top at the top level. */
export function insertionPoint(store: EditorStore): InsertAt {
  const scene = store.scene;
  const sel = store.state.selection;
  if (sel.length === 1) {
    const el = findElement(scene, sel[0]!);
    if (el && isContainer(el)) return { parentId: el.id };
    if (el) return { parentId: parentOf(scene, el.id)?.id ?? null, index: indexIn(scene, el.id) + 1 };
  }
  return { parentId: null };
}

function placeFor(scene: Scene, at: InsertAt, el: Element): { x: number; y: number } {
  const parent = at.parentId ? findElement(scene, at.parentId) : undefined;
  if (parent && parent.type === 'group' && parent.layout.mode === 'stack') return { x: 0, y: 0 };
  if (parent) return { x: 24, y: 24 };
  const { width, height } = nominalSize(el);
  const c = scene.canvas;
  if (el.type === 'text') return { x: Math.round(c.width * 0.1), y: Math.round(c.height * 0.72) };
  return { x: Math.round((c.width - width) / 2), y: Math.round((c.height - height) / 2) };
}

/** The font for new text: the scene's first font, else the first brand font, else the built-in one, else any font. */
export function defaultFont(scene: Scene): Asset | undefined {
  const inScene = scene.assets.find((a) => a.kind === 'font');
  if (inScene) return inScene;
  const lib = assetCache.library.filter((a) => a.kind === 'font');
  const brand = brandCache.kit.fonts.map((h) => lib.find((a) => a.hash === h)).find((a) => a !== undefined);
  return (brand ?? lib.find((a) => a.builtin) ?? lib[0])?.sceneAsset;
}

export function addElement(store: EditorStore, kind: NewElementKind, opts: { asset?: LibraryAsset } = {}): string | undefined {
  const at = insertionPoint(store);
  let scene = store.scene;
  let font: Asset | undefined;
  if (kind === 'text') {
    font = defaultFont(scene);
    if (!font) {
      window.alert(t('error.noFont'));
      return undefined;
    }
    scene = ensureAsset(scene, font);
  }
  if (opts.asset) scene = ensureAsset(scene, opts.asset.sceneAsset);
  const asset = opts.asset ? scene.assets.find((a) => a.hash === opts.asset!.hash) : undefined;
  const size = opts.asset?.meta.width && opts.asset.meta.height ? { width: opts.asset.meta.width, height: opts.asset.meta.height } : undefined;
  let el = createElement(scene, kind, { ...(font ? { font: font.hash } : {}), ...(asset ? { asset } : {}), ...(size ? { naturalSize: size } : {}) });
  el = { ...el, transform: { ...el.transform, ...placeFor(scene, at, el) } } as Element;
  const finalScene = addElements(scene, [el], at);
  store.apply(() => finalScene, { select: [el.id] });
  return el.id;
}

export function deleteSelection(store: EditorStore): void {
  const ids = store.state.selection;
  if (ids.length === 0) return;
  store.apply((s) => deleteElements(s, ids), { select: [] });
}

export function duplicateSelection(store: EditorStore): void {
  const ids = store.state.selection;
  if (ids.length === 0) return;
  // In every layout: each copy sits beside its original there.
  let copies: string[] = [];
  store.applyEach((s) => {
    const r = duplicate(s, ids);
    copies = r.ids;
    return r.scene;
  });
  store.select(copies);
}

export function groupSelection(store: EditorStore, kind: 'group' | 'clipMask' = 'group'): void {
  const ids = store.state.selection;
  if (ids.length === 0) return;
  let id: string | undefined;
  store.applyEach((s, geo) => {
    const r = groupElements(s, ids, geo, kind);
    id = r.id;
    return r.scene;
  });
  if (id) store.select([id]);
}

export function ungroupSelection(store: EditorStore): void {
  const groups = store.state.selection.filter((id) => isContainer(findElement(store.scene, id)));
  if (groups.length === 0) return;
  let released: string[] = [];
  store.applyEach((s, geo) => {
    const r = ungroup(s, groups, geo);
    released = r.ids;
    return r.scene;
  });
  store.select(released);
}

export function nudge(store: EditorStore, dx: number, dy: number): void {
  const ids = store.state.selection.filter((id) => !findElement(store.scene, id)?.locked);
  if (ids.length === 0) return;
  store.apply((s) => store.autoKeyed(s, moveBy(s, ids, dx, dy)), { key: `nudge:${ids.join(',')}` });
}

export function reorderSelection(store: EditorStore, to: 1 | -1 | 'front' | 'back'): void {
  const ids = store.state.selection;
  if (ids.length === 0) return;
  store.apply((s) => {
    // Forward: front-most first, so they do not swap with each other.
    const order = [...ids].sort((a, b) => (to === 1 || to === 'front' ? indexIn(s, b) - indexIn(s, a) : indexIn(s, a) - indexIn(s, b)));
    return order.reduce((acc, id) => reorder(acc, id, to), s);
  });
}

export function toggleVisible(store: EditorStore, ids = store.state.selection): void {
  if (ids.length === 0) return;
  const show = ids.some((id) => findElement(store.scene, id)?.visible === false);
  store.apply((s) => ids.reduce((acc, id) => patchElement(acc, id, { visible: show }), s));
}

export function toggleLocked(store: EditorStore, ids = store.state.selection): void {
  if (ids.length === 0) return;
  const lock = ids.some((id) => findElement(store.scene, id)?.locked === false);
  store.apply((s) => ids.reduce((acc, id) => patchElement(acc, id, { locked: lock }), s));
}

export function selectAll(store: EditorStore): void {
  const sel = store.state.selection;
  const scope = sel.length ? siblingsOf(store.scene, sel[0]!) : store.scene.elements;
  store.select(scope.filter((e) => !e.locked && e.visible).map((e) => e.id));
}

export function selectParent(store: EditorStore): void {
  const sel = store.state.selection;
  if (sel.length === 0) return;
  const p = parentOf(store.scene, sel[0]!);
  store.select(p ? [p.id] : []);
}

export function selectChildren(store: EditorStore): void {
  const sel = store.state.selection;
  const el = sel.length === 1 ? findElement(store.scene, sel[0]!) : undefined;
  if (el && isContainer(el) && el.children.length) store.select(el.children.map((c) => c.id));
  else if (el?.type === 'text') store.requestTextFocus();
}

export function alignSelection(store: EditorStore, how: Alignment): void {
  const ids = topmost(store.scene, store.state.selection);
  const rects = new Map(ids.flatMap((id) => {
    const b = store.geo.bounds(id);
    return b ? [[id, b] as const] : [];
  }));
  if (rects.size === 0) return;
  const c = store.scene.canvas;
  const to = rects.size === 1 ? { x: 0, y: 0, width: c.width, height: c.height } : unionRect([...rects.values()])!;
  store.apply((s) => moveElements(s, align(rects, how, to)));
}

export function distributeSelection(store: EditorStore, axis: 'x' | 'y'): void {
  const ids = topmost(store.scene, store.state.selection);
  const rects = new Map(ids.flatMap((id) => {
    const b = store.geo.bounds(id);
    return b ? [[id, b] as const] : [];
  }));
  if (rects.size < 3) return;
  store.apply((s) => moveElements(s, distribute(rects, axis)));
}

export function expose(store: EditorStore, elementId: string, property: Exposable): void {
  const r = exposeField(store.scene, elementId, property);
  store.apply(() => r.scene);
}

// ---------------------------------------------------------------------------------------------------------------
// Clipboard (document copy/cut/paste events: no permission prompt, works between graphics and tabs)

let lastCopy: ClipboardPayload | undefined;

export function copyToClipboard(store: EditorStore, e?: ClipboardEvent): boolean {
  const p = copySelection(store.scene, store.state.selection);
  if (!p) return false;
  lastCopy = p;
  const text = serializeClipboard(p);
  if (e?.clipboardData) {
    e.clipboardData.setData('text/plain', text);
    e.preventDefault();
  } else void navigator.clipboard?.writeText(text).catch(() => undefined);
  return true;
}

export function cutToClipboard(store: EditorStore, e?: ClipboardEvent): void {
  if (copyToClipboard(store, e)) deleteSelection(store);
}

export function pasteFromText(store: EditorStore, text: string | undefined): boolean {
  const p = (text ? parseClipboard(text) : undefined) ?? (text === undefined ? lastCopy : undefined);
  if (!p) return false;
  const existing = allIds(store.scene);
  const offset = p.elements.some((e) => existing.has(e.id)) ? 20 : 0;
  const at = insertionPoint(store);
  // Paste beside a selected element, not into it, unless it is a container.
  const r = paste(store.scene, p, at, offset);
  store.apply(() => r.scene, { select: r.ids });
  return true;
}

// ---------------------------------------------------------------------------------------------------------------
// Export

/** Save, then run the check (export → validation → headless smoke test → thumbnail) on the saved draft. */
export async function checkDraft(store: EditorStore): Promise<CheckReport> {
  await store.saveNow();
  return api.checkDraft(store.state.draftId);
}

/** Save, export and download the package zip. */
export async function exportPackage(store: EditorStore): Promise<void> {
  await store.saveNow();
  const { blob, fileName } = await api.exportDraft(store.state.draftId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Whether the selection (or any ancestor) is locked. */
export function isLockedPath(scene: Scene, id: string): boolean {
  return pathTo(scene, id).some((pid) => findElement(scene, pid)?.locked);
}
